import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Asterisk as AsteriskIcon } from "lucide-react";

/* =====================================================================
 * Mouse parallax
 * --------------------------------------------------------------------
 * The pitch deck is a slide-by-slide experience, not a scroll page, so
 * scroll-parallax does not apply. Instead we provide a *mouse* parallax
 * (cursor-relative micro-translation) that adds depth without breaking
 * the slide model or PDF print.
 *
 * `MouseParallaxStage` captures pointer position relative to itself and
 * exposes spring-smoothed -1..1 motion values via context. Children call
 * `useMouseParallaxOffset(strength)` to receive a translateX/translateY
 * pair calibrated to that tile.
 * ===================================================================== */

const MouseCtx = createContext(null);

export function MouseParallaxStage({ children, className = "" }) {
  const ref = useRef(null);
  const xRaw = useMotionValue(0);
  const yRaw = useMotionValue(0);
  const x = useSpring(xRaw, { stiffness: 80, damping: 18, mass: 0.4 });
  const y = useSpring(yRaw, { stiffness: 80, damping: 18, mass: 0.4 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const onMove = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
        xRaw.set(Math.max(-1, Math.min(1, nx)));
        yRaw.set(Math.max(-1, Math.min(1, ny)));
      });
    };
    const onLeave = () => {
      xRaw.set(0);
      yRaw.set(0);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, [xRaw, yRaw]);

  const value = useMemo(() => ({ x, y }), [x, y]);
  return (
    <MouseCtx.Provider value={value}>
      <div ref={ref} className={`relative ${className}`}>
        {children}
      </div>
    </MouseCtx.Provider>
  );
}

/** Returns spring-smoothed translate values in pixels (negative strength inverts). */
export function useMouseParallaxOffset(strength = 6) {
  const ctx = useContext(MouseCtx);
  // We MUST call hooks unconditionally, so create motion values
  // even if there's no parent stage (returns identity).
  const fallbackX = useMotionValue(0);
  const fallbackY = useMotionValue(0);
  const sourceX = ctx?.x ?? fallbackX;
  const sourceY = ctx?.y ?? fallbackY;
  const tx = useTransform(sourceX, (v) => v * strength);
  const ty = useTransform(sourceY, (v) => v * strength);
  return { x: tx, y: ty };
}

/* =====================================================================
 * BentoTile — opinionated card primitive with tonal variants
 * ===================================================================== */
const TONE_CLASS = {
  cream: "bento-cream",
  cyan: "bento-cyan",
  rose: "bento-rose",
  dark: "bento-dark text-1",
  glass: "bento-glass text-1",
};

export function BentoTile({
  tone = "glass",
  className = "",
  children,
  parallax = 0,
  delay = 0,
  testId,
  as = "div",
}) {
  const offset = useMouseParallaxOffset(parallax);
  const MotionComp = as === "section" ? motion.section : motion.div;
  return (
    <MotionComp
      data-testid={testId}
      initial={{ opacity: 0, y: 22, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.55, ease: [0.22, 0.65, 0.2, 1], delay }}
      style={parallax ? { x: offset.x, y: offset.y } : undefined}
      className={`relative overflow-hidden ${TONE_CLASS[tone] || TONE_CLASS.glass} ${className}`}
    >
      {children}
    </MotionComp>
  );
}

/* =====================================================================
 * BentoHeadline — editorial display type
 * ===================================================================== */
export function BentoHeadline({
  children,
  className = "",
  size = "lg", // "lg" | "xl"
  delay = 0,
  parallax = 0,
}) {
  const offset = useMouseParallaxOffset(parallax);
  return (
    <motion.h2
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.65, ease: [0.22, 0.65, 0.2, 1], delay }}
      style={parallax ? { x: offset.x, y: offset.y } : undefined}
      className={`bento-display ${size === "xl" ? "bento-display--xl" : ""} text-1 ${className}`}
    >
      {children}
    </motion.h2>
  );
}

/* =====================================================================
 * MetricTile — giant numeric anchor (01 / $180K / 99% / 15+)
 * ===================================================================== */
export function MetricTile({
  tone = "cream",
  eyebrow,
  value,
  label,
  sub,
  className = "",
  parallax = 0,
  delay = 0,
  testId,
}) {
  return (
    <BentoTile tone={tone} parallax={parallax} delay={delay} testId={testId} className={`p-7 sm:p-8 flex flex-col justify-between ${className}`}>
      {eyebrow && (
        <div className="text-3 text-[10px] uppercase tracking-[0.22em]">{eyebrow}</div>
      )}
      <div className="mt-6">
        <div className="font-semibold tabular-nums leading-[0.85] tracking-tighter text-1" style={{ fontSize: "clamp(3.2rem, 7.5vw, 7rem)" }}>
          {value}
        </div>
        {label && <div className="mt-3 text-1 text-sm font-medium leading-snug">{label}</div>}
        {sub && <div className="mt-1 text-3 text-[11px] leading-relaxed">{sub}</div>}
      </div>
    </BentoTile>
  );
}

/* =====================================================================
 * Asterisk corner mark — small rotating brand stamp
 * ===================================================================== */
export function Asterisk({ tone = "rose", size = 36, className = "" }) {
  const bg = {
    rose: "bg-rose-500 text-white",
    cyan: "bg-cyan-300 text-[#04111A]",
    cream: "bg-[#F4EFE6] text-[#0E1116]",
    dark: "bg-[#0E1116] text-cyan-300",
  }[tone];
  return (
    <span
      aria-hidden="true"
      className={`bento-asterisk ${bg} ${className}`}
      style={{ width: size, height: size }}
    >
      <AsteriskIcon style={{ width: size * 0.62, height: size * 0.62 }} strokeWidth={2.4} />
    </span>
  );
}

/* =====================================================================
 * NumberedCard — card with a giant outline number anchored beneath it
 * Used on the "What We Do" + "Team Blueprint" slides where each card is
 * numbered with a large outline-stroke number for editorial weight.
 * ===================================================================== */
export function NumberedCard({
  tone = "glass",
  number,
  icon: Icon,
  label,
  description,
  subs,
  className = "",
  parallax = 0,
  delay = 0,
  compact = false,
  numberAlign = "center", // "center" | "left"
  testId,
}) {
  const offset = useMouseParallaxOffset(parallax);
  // Outline color tracks the card tone for visual cohesion
  const outlineColor = {
    rose: "rgba(251, 113, 133, 0.55)",
    cyan: "rgba(103, 232, 249, 0.55)",
    cream: "rgba(244, 239, 230, 0.6)",
    dark: "rgba(255, 255, 255, 0.18)",
    glass: "rgba(255, 255, 255, 0.18)",
  }[tone] || "rgba(255, 255, 255, 0.18)";

  // Colored card background per tone (kept lighter than full bento variants
  // so the outline number remains the visual anchor below).
  const cardBg = {
    rose: "rounded-2xl border border-rose-400/30 bg-rose-500/[0.06] backdrop-blur-md",
    cyan: "rounded-2xl border border-cyan-300/30 bg-cyan-300/[0.06] backdrop-blur-md",
    cream: "bento-cream",
    dark: "rounded-2xl border border-white/8 bg-white/[0.03] backdrop-blur-md",
    glass: "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md",
  }[tone];

  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 0.65, 0.2, 1], delay }}
      style={parallax ? { x: offset.x, y: offset.y } : undefined}
      className={`relative ${className}`}
    >
      {/* Card */}
      <div className={`${cardBg} ${compact ? "p-4 text-center" : "p-5 sm:p-6"}`}>
        {Icon && (
          <Icon
            className={`${compact ? "h-5 w-5 mx-auto" : "h-6 w-6"} ${tone === "rose" ? "text-rose-300" : tone === "cyan" ? "text-cyan-200" : "text-cyan-300"}`}
            strokeWidth={1.6}
          />
        )}
        <div
          className={`font-semibold tracking-tight leading-[1.05] ${Icon ? "mt-3" : "mt-0"} ${
            tone === "rose" ? "text-rose-300" : tone === "cyan" ? "text-cyan-200" : "text-1"
          }`}
          style={{ fontSize: compact ? "clamp(1rem, 1.2vw, 1.15rem)" : "clamp(1.6rem, 2.4vw, 2.1rem)" }}
        >
          {label}
        </div>
        {description && (
          <p className="text-2 text-xs sm:text-[13px] mt-2.5 leading-relaxed">{description}</p>
        )}
        {subs && subs.length > 0 && (
          <ul className="mt-2 space-y-0.5">
            {subs.map((s) => (
              <li key={s} className="text-3 text-[10px]">{s}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Giant outline number — anchored below the card. Position varies by
          numberAlign: "center" centers under the card, "left" anchors flush
          with the left edge for a more editorial / reference-deck feel. */}
      <div
        aria-hidden="true"
        className={`absolute -bottom-[clamp(2rem,3vw,3.4rem)] pointer-events-none select-none ${
          numberAlign === "left"
            ? "left-0"
            : numberAlign === "right"
              ? "right-0"
              : "left-1/2 -translate-x-1/2"
        }`}
      >
        <span
          className="block font-semibold tabular-nums tracking-tighter leading-none"
          style={{
            fontSize: compact ? "clamp(3rem, 5vw, 5rem)" : "clamp(4.5rem, 7vw, 7rem)",
            WebkitTextStroke: `1.5px ${outlineColor}`,
            color: "transparent",
          }}
        >
          {number}
        </span>
      </div>
    </motion.div>
  );
}

export function BentoTag({ children, tone = "dark", className = "" }) {
  const cls = {
    dark: "bg-[#0E1116] text-[#F4EFE6] border-transparent",
    cream: "bg-[#F4EFE6] text-[#0E1116] border-[#0E1116]/10",
    cyan: "bg-cyan-300 text-[#04111A] border-transparent",
    rose: "bg-rose-500 text-white border-transparent",
    outline: "bg-transparent text-1 border-white/15",
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.22em] font-medium ${cls} ${className}`}
    >
      {children}
    </span>
  );
}
